/**
 * @lines98/client/components/dialog-plaque — примитив модальной плашки. Владеет
 * (1) визуальным контейнером (CSS, co-located dialog-plaque.css) И (2) ПРАВИЛОМ
 * единого размера текста своих контролов (этот файл).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner-law 2026-06-12, verbatim): «плашка-примитив; все её
 * текст-контролы — ОДНА группа; шрифт один = min, каждый от своего размера; равны
 * ПОТОМУ ЧТО велит плашка». Раньше каждая поверхность autofit'ила свои контролы врозь
 * (провайдер-кнопки — в auth-panel, сегмент — в segmented), и размеры РАСХОДИЛИСЬ.
 * Теперь правило живёт В ПРИМИТИВЕ-ПЛАШКЕ: на attach плашка собирает ВСЕ свои
 * текст-контролы (3 кнопки провайдеров + переключатель Вход/Регистрация + CTA формы) и
 * применяет ОДИН измеренный group-uniform размер = min по группе. Экран НЕ настраивает
 * autofit поштучно — это делает плашка (модель «ствол → ветки»).
 *
 * ГРУППА «РАСТЁТ ДО ЗАПОЛНЕНИЯ» — правило ТОЛЬКО плашки. Сегмент ВНЕ плашки
 * (лидерборд-табы) держит ПРЕЖНИЙ дизайн-размер (capToDesign, shrink-only) — это делает
 * сам примитив `segmented` для self-fit; плашка же перебивает его своим grow-to-fill
 * group-fit. Двойного autofit на одном элементе НЕТ: внутри плашки сегмент создаётся с
 * `selfAutofit:false` → им управляет ТОЛЬКО плашка (см. auth-panel / segmented).
 *
 * СТАБИЛЬНАЯ ВЫСОТНАЯ ОПОРА (owner-fix 2026-06-12 — корневой баг идемпотентности):
 * ширина каждого члена меряется от его ТЕКСТ-элемента, а высота-опора — от его КОНТРОЛА
 * (кнопка/таб с min-height --btn-h), не от контент-высоты текста (см. FitOptions.heightEl
 * в autofit-text). Иначе autofit залипал мелким при resize-вверх.
 *
 * SCOPE: `attachPlaqueGroupAutofit(plaque, collect) → handle`. `collect` re-вызывается
 * при КАЖДОМ замере → переживает re-render тела (раскрытие формы меняет состав группы).
 * Пересчёт по VIEWPORT_APPLIED_EVENT + trailing(~160ms) + fonts.ready + ResizeObserver
 * (как attachAutofit — skill §8.10-D).
 * NOT IN SCOPE: сам <dialog>/showModal (создаёт confirm-dialog / auth-modal), CSS-вид
 * (dialog-plaque.css), сетевые/форм-логика (auth-panel).
 *
 * Cross-references: components/dialog-plaque.css, lib/autofit-text (fitGroupUniform,
 * GroupMember, heightEl), components/segmented (selfAutofit), components/auth-panel.
 */

import './dialog-plaque.css';

import {
  fitGroupUniform,
  type GroupMember,
  type FitOptions,
} from '../lib/autofit-text.js';
import { VIEWPORT_APPLIED_EVENT } from '../lib/viewport-sizer.js';

/**
 * Floor читаемости текста контролов плашки (px) — тактильный/читаемость-минимум
 * (skill §0.A P2), НЕ верхний кап (рост вверх задаёт измерение). 13px = owner-минимум
 * CTA-шрифта. Единая константа для ВСЕЙ группы плашки (провайдеры + сегмент + CTA).
 */
export const PLAQUE_FS_MIN_PX = 13;

export interface PlaqueAutofitHandle {
  /** Пересчитать сейчас (напр. после re-render тела — раскрытия формы). */
  refit(): void;
  /** Снять слушатели/наблюдатель (на закрытии плашки). */
  detach(): void;
}

/**
 * Опции привязки плашки = FitOptions + «ведомые» (followers) элементы.
 *
 * FOLLOWERS (owner-directive 2026-06-12 — «Почта/Пароль либо того же размера, что
 * весь остальной текст, либо фиксированно меньше; не жить самим по себе»): текст
 * плашки, который НЕ участвует в вычислении group-min (не «драйвер» — его ширина не
 * меряется), но ПОЛУЧАЕТ итоговый размер группы × `followerRatio`. Так лейблы полей
 * формы привязаны к размеру остального текста плашки ОДНИМ источником (правило плашки),
 * а не живут своим токеном «где-то больше, где меньше». `followerRatio` = 1 (по
 * умолчанию) — тот же размер, что и группа; <1 — фиксированно меньше той же пропорцией.
 * `followers()` re-собирается при КАЖДОМ замере → переживает re-render тела.
 */
export interface PlaqueAutofitOptions extends FitOptions {
  /** Ведомые текст-элементы (получают размер группы × followerRatio). */
  followers?: () => readonly HTMLElement[];
  /** Доля размера группы для ведомых (1 = тот же; <1 = фиксированно меньше). */
  followerRatio?: number;
}

/**
 * Привязать group-uniform autofit к текст-контролам плашки. `collect` возвращает
 * актуальный список членов группы (re-вызывается при каждом замере → переживает
 * re-render). Каждый член — `GroupMember`: ширина от текст-элемента, высотная опора —
 * от его контрола (heightEl).
 *
 * Все члены получают ОДИН размер = min по группе (mode:'line' — каждый лейбл в одну
 * строку, ограничен шириной своего элемента И высотой своего контрола; group растёт до
 * заполнения = grow-to-fill, БЕЗ capToDesign — это правило плашки).
 *
 * `opts.followers` (если заданы) — текст-элементы, которые НЕ меряются (не драйверы
 * group-min), но получают ИТОГОВЫЙ размер группы × `opts.followerRatio` (по умолчанию 1).
 * Для лейблов полей формы: их размер привязан к остальному тексту плашки (owner-directive
 * 2026-06-12), а не живёт своим токеном. См. PlaqueAutofitOptions.
 */
export function attachPlaqueGroupAutofit(
  plaque: HTMLElement,
  collect: () => readonly GroupMember[],
  opts: PlaqueAutofitOptions = {},
): PlaqueAutofitHandle {
  let raf = 0;
  let trailing = 0;
  const { followers, followerRatio, ...fit } = opts;

  const run = (): void => {
    raf = 0;
    const members = collect();
    if (members.length === 0) return;
    // mode:'line' + group-uniform min; grow-to-fill (НЕТ capToDesign — owner-law: рост
    // до заполнения плашки = правило плашки). minPx = читаемость-floor (не кап).
    const size = fitGroupUniform(members, { mode: 'line', minPx: PLAQUE_FS_MIN_PX, ...fit });
    // Ведомые (лейблы полей) получают ИТОГОВЫЙ размер группы × followerRatio — привязаны
    // к остальному тексту плашки ОДНИМ источником (owner-directive 2026-06-12), а не
    // живут своим токеном. followerRatio=1 → тот же размер; <1 → фиксированно меньше.
    if (size != null && followers) {
      const followerPx = size * (followerRatio ?? 1);
      for (const f of followers()) f.style.fontSize = `${followerPx}px`;
    }
  };
  const schedule = (): void => {
    if (raf) return;
    raf = requestAnimationFrame(run);
  };
  // Пересчёт сейчас + trailing после settle (реверстка не мгновенна — см. attachAutofit).
  const scheduleSettled = (): void => {
    schedule();
    if (trailing) clearTimeout(trailing);
    trailing = window.setTimeout(schedule, 160);
  };

  scheduleSettled();

  let ro: ResizeObserver | null = null;
  if (typeof ResizeObserver !== 'undefined') {
    ro = new ResizeObserver(scheduleSettled);
    ro.observe(plaque);
  }

  const onGeom = (): void => scheduleSettled();
  if (typeof window !== 'undefined') {
    window.addEventListener(VIEWPORT_APPLIED_EVENT, onGeom);
  }

  if (typeof document !== 'undefined' && 'fonts' in document) {
    void document.fonts.ready.then(scheduleSettled);
  }

  return {
    refit: scheduleSettled,
    detach(): void {
      if (raf) cancelAnimationFrame(raf);
      if (trailing) clearTimeout(trailing);
      if (ro) ro.disconnect();
      if (typeof window !== 'undefined') {
        window.removeEventListener(VIEWPORT_APPLIED_EVENT, onGeom);
      }
    },
  };
}
